'use client';

import color from '@haaretz/l-color.macro';
import merge from '@haaretz/l-merge.macro';
import mq from '@haaretz/l-mq.macro';
import radius from '@haaretz/l-radius.macro';
import space from '@haaretz/l-space.macro';
import typesetter from '@haaretz/l-type.macro';
import useUser from '@haaretz/s-atoms/user';
import Button from '@haaretz/s-button';
import HtzImage from '@haaretz/s-htz-image';
import Modal from '@haaretz/s-modal';
import PpCounter from '@haaretz/s-pp-counter';
import RichText from '@haaretz/s-rich-text';
import isHtmlNode from '@haaretz/s-type-predicates/isHtmlNode';
import React from 'react';
import s9 from 'style9';

import type { PageDataFragment } from '@haaretz/s-fragments/PageData';
import type { SpecialSaleDataFragment } from '@haaretz/s-fragments/SpecialSaleData';

const c = s9.create({
  specialOffers: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    maxWidth: space(200),
    width: '100%',
    fontWeight: 700,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    columnGap: space(8),
    ...merge(
      mq({
        until: 'l',
        value: {
          rowGap: space(8),
          gridTemplateColumns: 'auto',
          gridTemplateRows: 'auto auto',
        },
      })
    ),
  },
  specialOffersWithCounter: {
    columnGap: space(0),
    textAlign: 'center',

    ...merge(
      mq({
        from: 'l',
        value: {
          marginBottom: space(10),
        },
      })
    ),
  },
  specialOffersWrapper: {
    display: 'flex',
    justifyContent: 'center',
    backgroundImage: `radial-gradient(circle at 0 0, ${color('ppSpecialOfferBgGStart')}, ${color(
      'ppSpecialOfferBgGEnd'
    )})` as string,
    marginTop: space(-25),
    marginBottom: space(-50),
    paddingTop: space(30),
    paddingBottom: space(60),
    zIndex: '-1',
  },
  specialOffersWrapperLoggedIn: {
    marginTop: space(-35),
    paddingTop: space(45),
  },
  specialOffersWrapperWithToggle: {
    marginBottom: space(-70),
    paddingBottom: space(70),
  },
  specialOffersExclusive: {
    ...typesetter(1),
    ...merge(
      mq({
        until: 'l',
        value: {
          order: -1,
          ...typesetter(-2),
        },
      })
    ),
  },
  exclusiveWithCounter: {
    marginBottom: space(2),
    ...merge(
      mq({
        until: 'l',
        value: {
          ...typesetter(0),
        },
      })
    ),
  },
  specialOffersTexts: {
    ...merge(
      mq({
        until: 'l',
        value: {
          order: 1,
          textAlign: 'center',
        },
      })
    ),
  },
  specialOffersTitle: {
    ...typesetter(5),
  },
  specialOffersImage: {
    width: '326px',
    ...merge(
      mq({
        until: 'l',
        value: {
          marginInlineStart: 'auto',
          marginInlineEnd: 'auto',
          width: '127px',
        },
      })
    ),
  },
  counter: {
    marginBottom: space(5),
    justifyContent: 'center',

    ...merge(
      mq({
        from: 'l',
        value: {
          marginBottom: space(6),
        },
      })
    ),
  },
  modal: {
    border: 'none',
    borderRadius: radius('xxLarge'),
    paddingRight: space(6),
    paddingLeft: space(6),
    paddingTop: space(15),
    paddingBottom: space(15),

    '::backdrop': {
      backgroundColor: color('neutral200', { opacity: 0.1 }),
      backdropFilter: 'blur(4px)',
    },

    ...merge(
      mq({
        until: 's',
        value: {
          marginBottom: 0,
          minWidth: '100%',
        },
      }),
      mq({
        from: 's',
        value: {
          maxWidth: space(129),
          paddingRight: space(23),
          paddingLeft: space(23),
          borderRadius: radius('xLarge'),
        },
      })
    ),
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: space(1),
    textAlign: 'center',
  },
  modalBtn: {
    marginTop: space(6),
  },
  modalTitle: {
    ...typesetter(5),

    ...merge(
      mq({
        from: 'xl',
        until: 'xxl',
        value: {
          ...typesetter(4),
        },
      }),
      mq({
        from: 'xxl',
        value: {
          ...typesetter(3),
        },
      })
    ),
  },
  richText: {
    ...merge(
      mq({ until: 's', value: { ...typesetter(3) } }),
      mq({ from: 's', until: 'm', value: { ...typesetter(3) } }),
      mq({ from: 'm', until: 'l', value: { ...typesetter(3) } }),
      mq({ from: 'l', until: 'xl', value: { ...typesetter(3) } }),
      mq({ from: 'xl', until: 'xxl', value: { ...typesetter(2) } }),
      mq({ from: 'xxl', value: { ...typesetter(1) } })
    ),
  },
});

interface SpecialOffersProps extends SpecialSaleDataFragment {
  showToggle: boolean;
  counter: PageDataFragment['counter'];
}
export default function SpecialOffers({
  exclusive,
  title,
  image,
  showToggle,
  counter,
}: SpecialOffersProps) {
  const user = useUser('cookieValue');
  const [isOpen, setIsOpen] = React.useState(false);
  const { endDate, message, buttonText, buttonLink, messageHeadline } = counter || {};
  const hasCounter = counter && !!endDate;
  const isFutureDate = endDate && Date.now() < endDate;

  return (
    <>
      <div
        className={s9(
          c.specialOffersWrapper,
          user?.isLoggedIn && c.specialOffersWrapperLoggedIn,
          showToggle && c.specialOffersWrapperWithToggle
        )}
        data-testid="special-offer-section"
      >
        <div className={s9(c.specialOffers, hasCounter && c.specialOffersWithCounter)}>
          <div className={s9(c.specialOffersTexts)}>
            {hasCounter && (
              <PpCounter endDate={endDate} styleExtend={[c.counter]} setIsOpen={setIsOpen} />
            )}
            <div
              className={s9(c.specialOffersExclusive, hasCounter && c.exclusiveWithCounter)}
              data-testid="special-offer-exclusive"
            >
              {exclusive}
            </div>
            <div className={s9(c.specialOffersTitle)} data-testid="special-offer-title">
              {title}
            </div>
          </div>
          {image && !hasCounter ? (
            <HtzImage
              {...image}
              styleExtend={[c.specialOffersImage]}
              imgData={image.files[0]}
              aspect="full"
              widths={[127, 254, 326]}
              data-testid="special-offer-img"
            />
          ) : null}
        </div>
      </div>
      {hasCounter && (
        <Modal
          styleExtend={[c.modal]}
          labelledBy={null}
          isOpen={!isFutureDate ? true : isOpen}
          onToggle={setIsOpen}
          forbidClosing
        >
          <div className={s9(c.modalContent)}>
            <h2 className={s9(c.modalTitle)}>{messageHeadline}</h2>
            {message
              ?.filter(isHtmlNode)
              .map(item => <RichText key={item.content} styleExtend={[c.richText]} {...item} />)}
            <Button
              styleExtend={[c.modalBtn]}
              as="a"
              href={buttonLink || '/offers'}
              variant="sales"
            >
              {buttonText}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
